import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from "@/components/catalyst/dropdown";
import { Avatar } from "@/components/catalyst/avatar";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { useOrgId } from "@/lib/GraphQLCodegen/fetcher";
import { useAuth } from "@/lib/Auth/useAuth";
import { useActiveOrganisation } from "@/app/Organisations/Hooks/useActiveOrganisation";
import { ChevronDownIcon } from "@heroicons/react/16/solid";
import {
  PlusIcon,
  RectangleGroupIcon,
  BuildingOffice2Icon,
  CheckIcon,
} from "@heroicons/react/20/solid";
import { useTranslations } from "use-intl";
import { useOrganisationSwitcher } from "@/app/Organisations/Hooks/useOrganisationSwitcher";
import { Link, useNavigate } from "@tanstack/react-router";
import { getInitials } from "@/lib/Utils/getInitials";
import { Suspense, useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { LanguageSelectorHeader } from "@/lib/i18n/LanguageSelector";

export function SidebarHeaderDropdown() {
  const { organisations } = useAuth();
  const { activeOrganisation } = useActiveOrganisation();
  const { switchOrganisation } = useOrganisationSwitcher();
  const navigate = useNavigate();
  const [orgOpen, setOrgOpen] = useState(false);

  const isAdmin = activeOrganisation?.is_admin ?? false;

  return (
    <div className="flex flex-col gap-2">
      {/* Imoova logo + language selector */}
      <div className="flex items-center justify-between px-2 pb-4">
        <Link to="/">
          <img
            src="/images/horizontal-logo.png"
            alt="Imoova"
            className="h-5 w-auto"
          />
        </Link>
        <LanguageSelectorHeader />
      </div>

      {/* Organisation pill selector */}
      <Popover open={orgOpen} onOpenChange={setOrgOpen}>
        <PopoverTrigger asChild>
          <button
            role="combobox"
            aria-expanded={orgOpen}
            className="flex w-full items-center gap-2.5 rounded-full border border-zinc-700 bg-zinc-800/50 px-2.5 py-1.5 text-left transition-colors hover:border-zinc-600 hover:bg-zinc-800"
          >
            <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-indigo-500/20">
              <BuildingOffice2Icon className="size-3.5 text-indigo-400" />
            </span>
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-zinc-200">
              {activeOrganisation?.name ?? "Select Organisation"}
            </span>
            <ChevronDownIcon className="size-4 shrink-0 text-zinc-500" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-0" align="start" side="bottom">
          <Command>
            <CommandInput placeholder="Search organisations..." />
            <CommandList>
              <CommandEmpty>No organisations found.</CommandEmpty>
              <CommandGroup>
                {organisations.map((org) => (
                  <CommandItem
                    key={org.id}
                    value={org.name}
                    onSelect={() => {
                      switchOrganisation(org);
                      setOrgOpen(false);
                    }}
                    className="flex items-center gap-2"
                  >
                    <Avatar
                      src={org.profile_picture_url ?? undefined}
                      initials={
                        org.profile_picture_url
                          ? undefined
                          : getInitials(org.name)
                      }
                      className={`size-5 ${org.profile_picture_url ? "bg-white" : ""}`}
                      square
                      objectFit="contain"
                    />
                    <span className="flex-1 truncate">{org.name}</span>
                    {org.id === activeOrganisation?.id ? (
                      <CheckIcon className="size-4 shrink-0 text-indigo-600" />
                    ) : null}
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                <CommandItem
                  onSelect={() => {
                    navigate({ to: "/onboarding/create-org" });
                    setOrgOpen(false);
                  }}
                  className="flex items-center gap-2"
                >
                  <PlusIcon className="size-4" />
                  <span>Create organisation</span>
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Supplier pill selector - hidden for admins. Isolated in its own
          Suspense so the supplier-list fetch fills the switcher in later and
          never blocks the rest of the shell from rendering. */}
      {!isAdmin ? (
        <Suspense fallback={<SupplierPillSkeleton />}>
          <SupplierSwitcher />
        </Suspense>
      ) : null}
    </div>
  );
}

function SupplierSwitcher() {
  const {
    supplier,
    supplierList,
    actions: { setSupplier },
  } = useGlobalSupplier();
  const orgId = useOrgId();
  const t = useTranslations("navigation");
  const navigate = useNavigate();
  const supplierIcon = supplier?.icon ?? supplier?.logo;

  return (
    <div className="relative">
      <Dropdown>
        <DropdownButton
          as="button"
          className="flex w-full items-center gap-2.5 rounded-full border border-zinc-700 bg-zinc-800/50 px-2.5 py-1.5 text-left transition-colors hover:border-zinc-600 hover:bg-zinc-800"
        >
          <span
            className={`inline-flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-full ${
              supplierIcon ? "bg-white" : "bg-amber-500/20"
            }`}
          >
            {supplierIcon ? (
              <img
                src={supplierIcon.url}
                alt={supplier.name}
                className="size-full object-contain"
              />
            ) : supplier ? (
              <span className="text-[10px] font-semibold text-amber-400">
                {getInitials(supplier.name)}
              </span>
            ) : (
              <RectangleGroupIcon className="size-3.5 text-amber-400" />
            )}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-zinc-200">
            {supplier ? supplier.name : t("allSuppliers")}
          </span>
          <ChevronDownIcon className="size-4 shrink-0 text-zinc-500" />
        </DropdownButton>
        <DropdownMenu className="min-w-64" anchor="bottom start">
          {supplierList.map((s) => {
            const iconUrl = (s.icon ?? s.logo)?.url;

            return (
              <DropdownItem
                key={s.id}
                onClick={() => {
                  setSupplier(s.id);
                }}
              >
                <Avatar
                  src={iconUrl ?? undefined}
                  initials={iconUrl ? undefined : getInitials(s.name)}
                  className={`size-6 ${iconUrl ? "bg-white" : ""}`}
                  square
                  objectFit="contain"
                />
                <DropdownLabel>{s.name}</DropdownLabel>
                {s.id === supplier?.id ? (
                  <span className="col-start-5">
                    <CheckIcon className="size-5 text-indigo-600" />
                  </span>
                ) : null}
              </DropdownItem>
            );
          })}

          {supplierList.length > 1 ? (
            <>
              <DropdownDivider />
              <DropdownItem
                onClick={() => {
                  setSupplier(null);
                }}
              >
                <RectangleGroupIcon />
                <DropdownLabel>{t("allSuppliers")}</DropdownLabel>
              </DropdownItem>
            </>
          ) : null}
          <DropdownDivider />
          <DropdownItem
            onClick={() =>
              orgId
                ? navigate({
                    to: "/org/$orgId/create-supplier",
                    params: { orgId },
                  })
                : undefined
            }
          >
            <PlusIcon />
            <DropdownLabel>Create supplier</DropdownLabel>
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
    </div>
  );
}

function SupplierPillSkeleton() {
  return (
    <div className="flex w-full items-center gap-2.5 rounded-full border border-zinc-700 bg-zinc-800/50 px-2.5 py-1.5">
      <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-amber-500/20">
        <RectangleGroupIcon className="size-3.5 text-amber-400" />
      </span>
      <span className="h-3.5 flex-1 animate-pulse rounded bg-zinc-700" />
      <ChevronDownIcon className="size-4 shrink-0 text-zinc-500" />
    </div>
  );
}
